/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: ba-ui-common (C) Copyright IBM Corp. 2017, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import glassDnD from 'bi-glass-dnd-manager';
import {
	getNodeClientOffset,
	getNodeDepth,
	getEventClientOffset,
	offsetWithinBoundingRect,
} from './domUtils';

class GlassDnDBackend {
	constructor(
		manager,
		{ deadzone = 5, glassDnD: glassDnDInst }: { deadzone?: number; glassDnD?: {} } = {}
	) {
		this.actions = manager.getActions();
		this.monitor = manager.getMonitor();
		this.registry = manager.getRegistry();
		this.targetNodes = {};
		this.sourceNodes = {};
		this.sourceNodesOptions = {};
		this.targetDepths = {};
		this.mouseClientOffset = {};

		this.deadzone = deadzone;
		this._glassDnD = glassDnDInst;
	}

	actions: Record<string, any>;
	monitor: Record<string, any>;
	registry: Record<string, any>;
	deadzone: number;
	_glassDnD: {};
	mouseClientOffset: {};
	targetNodes: Record<string, HTMLElement>;
	sourceNodes: Record<string, HTMLElement>;
	sourceNodesOptions: Record<string, any>;
	useAccepts: boolean = true;
	dropAllowed: boolean = false;
	targetDepths: Record<string, number>;
	_externalTargets: string[];
	_timeoutId: number;

	get glassDnD() {
		return this._glassDnD || glassDnD;
	}

	setup() {
		if (typeof window === 'undefined') {
			return;
		}

		window.addEventListener('keydown', this.handleWindowKeyDown, true);
		window.addEventListener('mouseup', this.handleClear, true);
		window.addEventListener('touchend', this.handleClear, true);
	}

	getSourceClientOffset = sourceId => getNodeClientOffset(this.sourceNodes[sourceId]);

	teardown() {
		if (typeof window === 'undefined') {
			return;
		}

		this.mouseClientOffset = {};
		this.targetNodes = {};
		this.targetDepths = {};
		window.removeEventListener('keydown', this.handleWindowKeyDown, true);
		window.removeEventListener('mouseup', this.handleClear, true);
		window.removeEventListener('touchend', this.handleClear, true);
	}

	connectDragSource(sourceId, node, options) {
		this.sourceNodes[sourceId] = node;
		this.sourceNodesOptions[sourceId] = options || {};

		const handleMouseMoveStart = this.handleMouseMoveStart.bind(this, sourceId);
		const handleTouchMoveStart = this.handleTouchMoveStart.bind(this, sourceId);

		node.addEventListener('mousedown', handleMouseMoveStart);
		node.addEventListener('touchstart', handleTouchMoveStart);

		return () => {
			delete this.sourceNodes[sourceId];
			delete this.sourceNodesOptions[sourceId];
			node.removeEventListener('mousedown', handleMouseMoveStart);
			node.removeEventListener('touchstart', handleTouchMoveStart);
		};
	}

	connectDragPreview() {
		return false;
	}

	registerExternalDropTargets() {
		const { dropTargets } = this.glassDnD;

		this._externalTargets &&
			this._externalTargets.forEach(targetId => {
				this.registry.removeTarget(targetId);
				delete this.targetDepths[targetId];
				delete this.targetNodes[targetId];
			});

		this._externalTargets = [];

		const allTypes = Object.keys(
			Object.keys(this.registry.types).reduce((typeMap, key) => {
				if (this.sourceNodes[key]) {
					typeMap[this.registry.types[key]] = true;
				}

				return typeMap;
			}, {})
		);

		dropTargets.forEach(({ callbacks = {}, el, selector }) => {
			if (el && selector !== '.dialogBlocker' && !callbacks.REACT_DND_INTERNAL) {
				const els = el.length ? Array.prototype.slice.call(el) : [el];

				els.forEach(domEl => {
					const targetId = this.registry.addTarget(allTypes, {
						canDrop: () =>
							this.useAccepts
								? !callbacks.accepts || callbacks.accepts(this.glassDnD.dragObject)
								: this.dropAllowed,
						hover() {},
						drop() {},
					});

					this._externalTargets.push(targetId);
					this.targetDepths[targetId] = getNodeDepth(domEl);
					this.targetNodes[targetId] = domEl;
				});
			}
		});
	}

	connectDropTarget(targetId, node) {
		this.targetDepths[targetId] = getNodeDepth(node);
		this.targetNodes[targetId] = node;

		const unsubscribe = this.glassDnD.addDropTarget(node, {
			priority: this.targetDepths[targetId],
			REACT_DND_INTERNAL: true,
		}).remove;

		return () => {
			unsubscribe();
			delete this.targetNodes[targetId];
			delete this.targetDepths[targetId];
		};
	}

	handleWindowKeyDown = e => {
		// esc key
		if (e.keyCode === 27) {
			if (this.monitor.isDragging()) {
				this.actions.endDrag();
			}
		}
	};

	handleMouseMoveStart(sourceId, e) {
		if (e.which !== 1) {
			return;
		}

		const target = e.currentTarget;
		// settimeout used to give react events a chance to fire first

		this._timeoutId = window.setTimeout(() => this.handleMoveStart(sourceId, e, target), 0);
	}

	handleTouchMoveStart(sourceId, e) {
		if (!e.touches || e.touches.length !== 1) {
			return;
		}

		const target = e.currentTarget;
		// settimeout used to give react events a chance to fire first

		this._timeoutId = window.setTimeout(() => this.handleMoveStart(sourceId, e, target), 0);
	}

	handleClear = () => {
		clearTimeout(this._timeoutId);
	};

	handleMoveStart(sourceId, e, target) {
		const handler = this.registry.handlers[sourceId];

		if (!handler || !handler.canDrag || !handler.canDrag()) {
			return;
		}

		const options = this.sourceNodesOptions[sourceId];
		let xPos = null;
		let yPos = null;

		if (options.restrictToYAxis) {
			xPos = target.getBoundingClientRect().left;
		}

		if (options.restrictToXAxis) {
			yPos = target.getBoundingClientRect().top;
		}

		this.glassDnD.startDrag({
			...options,
			event: e,
			moveXThreshold: this.deadzone,
			moveYThreshold: this.deadzone,
			callerCallbacks: {
				onDragStart: eDrag => {
					this.dropAllowed = false;
					this.useAccepts = true;
					this.registerExternalDropTargets();
					this.actions.beginDrag([sourceId], {
						clientOffset: getEventClientOffset(eDrag),
						getSourceClientOffset: this.getSourceClientOffset,
						publishSource: false,
					});

					if (!this.monitor.isDragging()) {
						// This can occur of `canDrag` returns `false` from a react-dnd dragSource
						this.glassDnD.resetDragging();
					} else {
						this.actions.publishDragSource();
						const { dragObject } = this.glassDnD;

						dragObject.type = this.monitor.getItemType();
						dragObject.data = this.monitor.getItem();
					}
				},
				onDragDone: () => {
					this.dropAllowed = false;
					this.useAccepts = true;
					if (this.monitor.isDragging()) {
						this.actions.drop();
						this.actions.endDrag();
					}
				},
				onMove: (eDrag, data) => {
					const { dragObject, dropTargetNode } = data;

					this.useAccepts = !('dropTargetNode' in data);
					this.dropAllowed = !!dropTargetNode;
					if (this.monitor.isDragging()) {
						const clientOffset = getEventClientOffset(eDrag);

						if (xPos != null) {
							clientOffset.x = xPos;
						}

						if (yPos != null) {
							clientOffset.y = yPos;
						}

						const matchingTargetIds = Object.keys(this.targetNodes)
							.filter(targetId => {
								// IE11 will throw if we do a getBoundingClientRect on an element that is not in the DOM
								// this try/catch will ignore those elements
								try {
									const boundingRect = this.targetNodes[
										targetId
									].getBoundingClientRect();

									return offsetWithinBoundingRect(clientOffset, boundingRect);
								} catch (e) {
									return false;
								}
							})
							.sort(
								(targetIdA, targetIdB) =>
									this.targetDepths[targetIdA] - this.targetDepths[targetIdB]
							);

						dragObject.clientOffset = clientOffset;

						this.actions.hover(matchingTargetIds, {
							clientOffset,
						});
					}
				},
			},
		});
	}
}

export default function createBackend(optionsOrManager: { getMonitor?: any } = {}) {
	const backendFactory = function(manager) {
		return new GlassDnDBackend(manager, optionsOrManager as any);
	};

	if (optionsOrManager.getMonitor) {
		return backendFactory(optionsOrManager);
	}

	return backendFactory;
}
